import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { getAllRouteData } from '../../../lib/get-all-route-data';
import { throttle } from '../../../lib/throttle';
import { PICFLOW_STORYBLOK } from '../../../picflow-storyblok';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { StoryblokCdnPipe } from '../../pipes/storyblok-cdn.pipe';
import { StoryblokRichTextPipe } from '../../storyblok-rich-text.pipe';
import { StripOuterPTagPipe } from '../../strip-outer-p-tag.pipe';
import { BlockStyle, BlockWrapperComponent } from '../block-wrapper/block-wrapper.component';
import { Review } from '../review-list/review-list.component';

@Component({
  selector: 'app-review-carousel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './review-carousel.component.html',
  styleUrl: './review-carousel.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [BlockWrapperComponent, SvgComponent, StoryblokRichTextPipe, StripOuterPTagPipe, StoryblokCdnPipe],
})
export class ReviewCarouselComponent implements OnChanges, AfterViewInit {
  private route = inject(ActivatedRoute);
  private storyblokService = inject(PICFLOW_STORYBLOK);
  @Input() _uid: string; // just here to prevent errors
  @Input() component: string; // just here to prevent errors
  @Input() _editable: string;
  @Input() style: BlockStyle[];

  @Input() reviews: { review: string; _editable: string; rating: string }[];
  @Input() reviewData: Review[];

  @ViewChild('carouselEl') carouselEl: ElementRef<HTMLDivElement>;
  @ViewChildren('reviewEl') reviewEls: QueryList<ElementRef<HTMLDivElement>>;

  index = 0;
  height: number;

  ngAfterViewInit(): void {
    this.height = this.reviewEls.get(0)?.nativeElement?.offsetHeight || 0;
  }

  ngOnChanges(): void {
    const data = getAllRouteData(this.route.snapshot);
    const relationships = data.relationships;
    this.reviewData = this.reviews
      .map(review => {
        const relationship = relationships.find(r => r.uuid === review.review)?.content;
        if (!relationship) return;
        return {
          ...relationship,
          _editable: review._editable,
          stars: this.getStars(Number(relationship.rating)),
          link: this.storyblokService.getUrlLink(relationship.source),
        };
      })
      .filter(Boolean);
  }

  getStars(stars: number) {
    const count = Number(stars) || 0;
    return Array(5)
      .fill(0)
      .map((_, i) => {
        const _i = i + 1;
        if (_i >= count + 1) return 'star-empty';
        if (_i <= count) return 'star-filled';
        return 'star-half';
      });
  }

  scrollToIndex(smooth: boolean) {
    this.carouselEl.nativeElement.scrollTo({
      left: this.index * this.carouselEl.nativeElement.offsetWidth,
      behavior: smooth ? 'smooth' : 'instant',
    });
    this.height = this.reviewEls.get(this.index).nativeElement.offsetHeight;
  }

  onScroll = throttle(() => {
    const left = this.carouselEl.nativeElement.scrollLeft;
    this.index = Math.round(left / this.carouselEl.nativeElement.offsetWidth);
    this.height = this.reviewEls.get(this.index).nativeElement.offsetHeight;
  }, 100);

  next(smooth: boolean) {
    if (this.index === this.reviewData.length - 1) {
      this.index = 0;
    } else {
      this.index += 1;
    }
    this.scrollToIndex(smooth);
  }

  previous(smooth: boolean) {
    if (this.index === 0) {
      this.index = this.reviewData.length - 1;
    } else {
      this.index -= 1;
    }
    this.scrollToIndex(smooth);
  }

  goTo(index: number, smooth: boolean) {
    this.index = index;
    this.scrollToIndex(smooth);
  }
}
